// src/lib/targetService.ts
//
// BekalNanti — P3 Target Specifications Data Service
// Interacts with canonical schema: bekalnanti_app (table: goal_targets)
// Tenant isolation and RLS enforced via goal_targets_all_member.

import { supabase } from './supabase'
import { TargetSpecificationRecord } from './targetSpecification'

/**
 * Map raw database row from bekalnanti_app.goal_targets to TargetSpecificationRecord.
 */
export function mapRawGoalTarget(row: any): TargetSpecificationRecord {
  return {
    id: row.id,
    goalId: row.goal_id,
    targetStage: row.target_stage ?? null,
    programName: row.program_name ?? null,
    targetCountry: row.target_country ?? null,
    targetRegion: row.target_region ?? null,
    targetCity: row.target_city ?? null,
    institutionId: row.institution_id ?? null,
    institutionName: row.institution_name ?? null,
    institutionType: row.institution_type ?? null,
    targetYear: row.target_year ? Number(row.target_year) : (row.target_date ? new Date(row.target_date).getFullYear() : null),
    targetDate: row.target_date ?? null,
    durationMonths: row.duration_months ? Number(row.duration_months) : null,
    targetCurrency: row.target_currency ?? 'IDR',
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }
}

/**
 * Fetch a single target specification by goalId.
 */
export async function fetchGoalTarget(goalId: string): Promise<TargetSpecificationRecord | null> {
  const { data, error } = await supabase
    .from('goal_targets')
    .select('*')
    .eq('goal_id', goalId)
    .maybeSingle()

  if (error) {
    throw new Error(`Gagal memuat spesifikasi sasaran: ${error.message}`)
  }

  return data ? mapRawGoalTarget(data) : null
}

/**
 * Fetch all target specifications for a list of goal IDs in bulk.
 * Returns a map of goalId -> TargetSpecificationRecord.
 */
export async function fetchHouseholdGoalTargets(
  goalIds: string[]
): Promise<Record<string, TargetSpecificationRecord>> {
  if (goalIds.length === 0) return {}

  const { data, error } = await supabase
    .from('goal_targets')
    .select('*')
    .in('goal_id', goalIds)

  if (error) {
    throw new Error(`Gagal memuat daftar spesifikasi sasaran: ${error.message}`)
  }

  const map: Record<string, TargetSpecificationRecord> = {}
  for (const row of data || []) {
    map[row.goal_id] = mapRawGoalTarget(row)
  }
  return map
}

/**
 * Create or update a target specification for a goal.
 */
export async function upsertGoalTarget(
  target: TargetSpecificationRecord
): Promise<TargetSpecificationRecord> {
  // If targetDate is not provided but targetYear is, synthesize target_date e.g. YYYY-08-01
  let targetDate = target.targetDate ?? null
  if (!targetDate && target.targetYear) {
    targetDate = `${target.targetYear}-08-01`
  }

  const payload: any = {
    goal_id: target.goalId,
    target_stage: target.targetStage || null,
    program_name: target.programName || null,
    target_country: target.targetCountry || null,
    target_region: target.targetRegion || null,
    target_city: target.targetCity || null,
    institution_id: target.institutionId || null,
    institution_name: target.institutionName || null,
    institution_type: target.institutionType || null,
    target_year: target.targetYear ? Number(target.targetYear) : null,
    target_date: targetDate,
    duration_months: target.durationMonths ? Number(target.durationMonths) : null,
    target_currency: target.targetCurrency || 'IDR',
    updated_at: new Date().toISOString(),
  }

  // If target has existing id, update directly
  if (target.id) {
    const { data, error } = await supabase
      .from('goal_targets')
      .update(payload)
      .eq('id', target.id)
      .select()
      .single()

    if (error) {
      throw new Error(`Gagal memperbarui spesifikasi sasaran: ${error.message}`)
    }
    return mapRawGoalTarget(data)
  }

  // Check if a target already exists for this goal_id to avoid duplication
  const existing = await fetchGoalTarget(target.goalId)
  if (existing?.id) {
    const { data, error } = await supabase
      .from('goal_targets')
      .update(payload)
      .eq('id', existing.id)
      .select()
      .single()

    if (error) {
      throw new Error(`Gagal memperbarui spesifikasi sasaran: ${error.message}`)
    }
    return mapRawGoalTarget(data)
  }

  // Otherwise insert new row
  const { data, error } = await supabase
    .from('goal_targets')
    .insert(payload)
    .select()
    .single()

  if (error) {
    throw new Error(`Gagal menyimpan spesifikasi sasaran: ${error.message}`)
  }

  return mapRawGoalTarget(data)
}

/**
 * Delete a target specification by its target ID.
 */
export async function deleteGoalTarget(targetId: string): Promise<void> {
  const { error } = await supabase
    .from('goal_targets')
    .delete()
    .eq('id', targetId)

  if (error) {
    throw new Error(`Gagal menghapus spesifikasi sasaran: ${error.message}`)
  }
}
